import { Box, Divider } from '@eatclubapp/ec-component-library';
import { CircularProgress, useTheme } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { ReactComponent as IconDate } from '../../../assets/icons/date.svg';
import { ReactComponent as IconGuests } from '../../../assets/icons/guests.svg';
import { ReactComponent as IconTime } from '../../../assets/icons/time.svg';
import { Button } from '../../../components/Button';
import CustomBookingFields from '../../../components/CustomBookingFields/CustomBookingFields';
import { Spacer } from '../../../components/Spacer';
import { Typography } from '../../../components/Typography';
import { updateBookingAction, updateBookingStepAction } from '../../../data/actions/bookingAction';
import { fetchBookingTermsAction } from '../../../data/actions/bookingTermsAction';
import {
  fetchAddonsAction,
  holdAccommodationAction,
  holdExperiencesAction,
} from '../../../data/actions/experienceAction';
import { freeBookingAction, holdBookingAction } from '../../../data/actions/obeeBookingAction';
import { restaurantsByIdsAction } from '../../../data/actions/restaurantAction';
import {
  bookingKeys,
  bookingPropTypes,
  bookingSteps,
  isEditing,
} from '../../../data/models/Booking';
import { bookingTermsPropTypes } from '../../../data/models/BookingTerms';
import { eventPropTypes } from '../../../data/models/Event';
import { addonGroupsPropTypes, experiencesPropTypes } from '../../../data/models/Experience';
import { restaurantPropTypes, restaurantsPropTypes } from '../../../data/models/Restaurant';
import { obeeAvailabilitySuggestionsPropTypes } from '../../../data/models/obeeAvailabilitySuggestions';
import { STATUS_TYPES, obeeBookingPropTypes } from '../../../data/models/obeeBooking';
import { obeeMultiVenueAvailabilitySuggestionsPropTypes } from '../../../data/models/obeeMultiVenueAvailabilitySuggestions';
import { PageLayout } from '../../../layouts/PageLayout';
import { WidgetLayout } from '../../../layouts/WidgetLayout';
import { trackEvent } from '../../../utils/analytics';
import { getAddonGroupsForBooking } from '../../../utils/bookingHelpers';
import {
  getCustomBookingFields,
  validateCustomBookingFields,
} from '../../../utils/customBookingFields';
import { devLog } from '../../../utils/devLog';
import {
  formatDateToMoment,
  formatDateWithYear,
  getFixedGridColumns,
  isEmpty,
  isNil,
  minutesToTimeString,
  onlyUnique,
  sortByProperty,
} from '../../../utils/helpers';
import { useIsMobile } from '../../../utils/hooks';
import { safeParse } from '../../../utils/stringHelpers';
import { CancelledBooking } from '../../CancelledBooking';
import { Areas } from './Areas';
import useStyles from './AvailabilityStyles';
import { Guests } from './Guests';
import { MultiVenueNextAvailableTimes } from './MultiVenueNextAvailableTimes';
import { NextAvailableTimes } from './NextAvailableTimes';
import { SelectADate } from './SelectADate';
import { SelectExperience } from './SelectExperience';

const Availability = ({
  booking,
  obeeMultiVenueAvailabilitySuggestions,
  obeeBooking,
  restaurant,
  restaurants,
  restaurantsByIds,
  experiences,
  obeeAvailabilitySuggestions,
  updateBookingStep,
  updateBooking,
  holdBooking,
  freeBooking,
  holdExperiences,
  holdAccommodation,
  addonGroups,
  fetchAddons,
  event,
  isEventMode,
  fetchBookingTerms,
  bookingTerms,
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const [multiVenueRestIds, setMultiVenueRestIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customFieldErrors, setCustomFieldErrors] = useState({});

  const isCancelled = [
    STATUS_TYPES.NO_SHOW,
    STATUS_TYPES.CANCELLED,
    STATUS_TYPES.UNCONFIRMED,
  ].includes(obeeBooking.data.status);

  const isMobile = useIsMobile();

  const showAreas = restaurant.data.areas.length > 1;
  const isUsingExperiences = !isEmpty(experiences.data);
  const requireExperienceSelection = experiences?.requireExperienceSelection;

  const showTermsSecondStep = safeParse(restaurant?.data?.bookingWidgetSettings)?.widgetSettings
    ?.settingsDetails?.showTermsSecondStep;
  const firstPageCustomFields = getCustomBookingFields(restaurant, 'firstPage');

  // Determine whether to show available times based on experience settings
  const showAvailableTimes = useMemo(() => {
    if (isEventMode && (!booking?.date || experiences?.pending)) {
      return false;
    }

    if (!isUsingExperiences) {
      return true;
    }
    // If using experiences and selection is required, only show available times when any experience is selected
    if (requireExperienceSelection) {
      return !isEmpty(booking?.selectedExperiences);
    }

    // If using experiences but selection is not required, show available times by default
    return true;
  }, [isUsingExperiences, booking?.selectedExperiences, requireExperienceSelection]);

  const ref = useRef(null);
  const nextButtonRef = useRef(null);

  useEffect(() => {
    const newMultiVenueRestIds = obeeMultiVenueAvailabilitySuggestions.data
      .map((availabilitySuggestion) => availabilitySuggestion.restId)
      .filter(onlyUnique);

    setMultiVenueRestIds(newMultiVenueRestIds);
  }, [obeeMultiVenueAvailabilitySuggestions.data]);

  useEffect(() => {
    // check which venues have been cached
    const restIds = multiVenueRestIds.filter((restId) =>
      restaurants.data.every((rest) => rest.objectId !== restId),
    );

    if (restIds.length > 0) {
      restaurantsByIds(restIds);
    }
  }, [multiVenueRestIds]);

  const showMultiVenue = !isEditing(booking) && multiVenueRestIds.length > 0;

  if (isCancelled) {
    return <CancelledBooking />;
  }

  // Cache previous suggestion
  const previousSuggestion = useMemo(() => booking?.availabilitySuggestion, []);

  const submit = async () => {
    trackEvent('Navigation', 'Updating Step', 'Next Button');

    const customFieldsValidation = validateCustomBookingFields(
      firstPageCustomFields,
      booking.customBookingFieldValues,
    );
    setCustomFieldErrors(customFieldsValidation);
    if (Object.keys(customFieldsValidation).length > 0) {
      return;
    }

    try {
      setIsSubmitting(true);
      // Wait for fetchAddons to complete
      await fetchAddons({
        areaId: booking?.availabilitySuggestion?.table?.areaId ?? booking?.areaId,
        size: booking.guests,
        date: booking.date,
        time: minutesToTimeString(booking.availabilitySuggestion?.time, 'HH:mm:ss'),
      });

      // TODO move to helper or somewhere
      const getOptimalArea = () => {
        const areaForBooking = restaurant.data.areas.find(
          (area) => area.id === booking.availabilitySuggestion?.table?.areaId ?? booking?.areaId,
        );

        if (areaForBooking) {
          return areaForBooking;
        }

        // NOTE: Find the optimal area for "all areas" waitlist
        const potentialAreas = restaurant.data.areas.filter(
          (area) => area.minGroup <= booking.guests && area.maxGroup >= booking.guests,
        );

        return potentialAreas.sort((a, b) => sortByProperty(a, b, 'priority'))[0];
      };

      // If creating booking and booking ID was already held, free that booking
      const hasChanged =
        previousSuggestion?.table?.areaId !== booking?.availabilitySuggestion?.table?.areaId ||
        previousSuggestion?.table?.tableNumber !==
          booking?.availabilitySuggestion?.table?.tableNumber ||
        previousSuggestion?.time !== booking?.availabilitySuggestion?.time;

      if (showTermsSecondStep) {
        fetchBookingTerms(
          booking?.availabilitySuggestion?.table?.areaId,
          booking?.date,
          booking?.availabilitySuggestion?.time,
          booking?.guests,
        );
      }

      // TODO currently there is no support for holding a timeslot while editing a booking
      // Free the old booking (if any), then hold the new
      let heldBookingId = booking?.heldBookingId;
      if (hasChanged && !isEditing(booking) && heldBookingId) {
        await freeBooking(heldBookingId);
        heldBookingId = null;
      }
      const fromEventWidget = event?.data != null;
      // Hold the booking
      if (!isEditing(booking) && !heldBookingId && !booking.waitlist) {
        await holdBooking(
          booking,
          booking?.availabilitySuggestion?.table?.areaId,
          booking?.availabilitySuggestion?.table?.tableNumber,
          getOptimalArea()?.duration,
          fromEventWidget,
          (holdBookingResponse) => {
            // Hold experiences if there are any
            if (!isEmpty(booking?.selectedExperiences)) {
              // Hold experiences UNLESS it's accommodation

              // If it's an accommodation booking, hit a different to get the tables
              const accommodationExperiences = booking?.selectedExperiences?.filter(
                (experience) => experience.experience.experienceTypeSettings.accommodation,
              );

              // If it's an accommodation booking, you need to hold the experience a second time to get the tables
              const normalExperiences = booking?.selectedExperiences?.filter(
                (experience) => !experience.experience.experienceTypeSettings.accommodation,
              );

              // Hold the experiences that aren't accommodation
              if (!isEmpty(normalExperiences)) {
                holdExperiences(holdBookingResponse?.id, normalExperiences, booking?.addons);
              }

              // Hold the accommodation experiences
              if (!isEmpty(accommodationExperiences)) {
                holdAccommodation(
                  holdBookingResponse?.id,
                  booking.guests,
                  accommodationExperiences,
                  booking?.availabilitySuggestion?.table?.id,
                );
              }
            }
          },
        );
      }
    } catch (error) {
      setIsSubmitting(false);
      devLog('Error fetching addons:', error);
    }
  };

  const updateCustomBookingField = (fieldId, value) => {
    setCustomFieldErrors((errors) => ({ ...errors, [fieldId]: null }));
    updateBooking(bookingKeys.customBookingFieldValues, {
      ...(booking.customBookingFieldValues || {}),
      [fieldId]: value,
    });
  };

  useEffect(() => {
    // If there is a need to jump to Term page, only proceed after fetched bookingTerms
    if (showTermsSecondStep && bookingTerms?.pending) {
      return;
    }
    // Only proceed if we're in submitting state and addons fetch completed
    if (isSubmitting && !addonGroups.pending) {
      setIsSubmitting(false);

      if (!addonGroups.success) {
        devLog('Failed to fetch addons');
        return;
      }

      const addonGroupsForBooking = getAddonGroupsForBooking(booking, addonGroups);

      const experienceHasTermsBeforeAddons = booking?.selectedExperiences?.some(
        (experience) =>
          experience?.experience?.showTermsSeparately &&
          !isEmpty(experience?.experience?.termsAndConditions),
      );

      if (showTermsSecondStep && bookingTerms?.success && !isEmpty(bookingTerms?.data)) {
        updateBookingStep(bookingSteps.terms);
        return;
      }

      if (experienceHasTermsBeforeAddons) {
        updateBookingStep(bookingSteps.terms);
        return;
      }

      if (!isEmpty(addonGroupsForBooking.beforeDetails)) {
        updateBookingStep(bookingSteps.addonsBeforeDetails, 0);
      } else {
        updateBookingStep(bookingSteps.details, 0);
      }
    }
  }, [
    addonGroups.pending,
    addonGroups.success,
    isSubmitting,
    bookingTerms?.success,
    bookingTerms?.pending,
  ]);

  const nextButtonDisabled =
    obeeAvailabilitySuggestions.pending ||
    (isUsingExperiences && isEmpty(booking?.selectedExperiences) && requireExperienceSelection) ||
    isNil(booking.availabilitySuggestion) ||
    (booking.availabilitySuggestion?.restId &&
      booking.availabilitySuggestion?.restId !== restaurant.data.objectId) ||
    booking.availabilitySuggestion?.waitlist;

  useEffect(() => {
    if (!nextButtonDisabled && nextButtonRef?.current) {
      nextButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [nextButtonDisabled]);

  const message = event?.data?.descriptionHTML ?? restaurant.data.venueMessage;

  const eventDates = event?.data?.eventDates;
  const isSpecificEventDate = eventDates?.length === 1;

  if (isEventMode && (isEmpty(booking.date) || isEmpty(experiences.data))) {
    return (
      <PageLayout>
        <WidgetLayout>
          <Spacer direction='vertical' gap='m' style={{ width: '100%' }}>
            {!isEmpty(message) && (
              <Typography centered>
                <div dangerouslySetInnerHTML={{ __html: message }} />
              </Typography>
            )}
            {!isEmpty(message) && <Divider color={theme.colors.dividerColor} />}
            <Guests />
            <Divider color={theme.colors.dividerColor} />
            <SelectADate />
            <CustomBookingFields
              fields={firstPageCustomFields}
              values={booking.customBookingFieldValues}
              errors={customFieldErrors}
              onChange={updateCustomBookingField}
            />
          </Spacer>
        </WidgetLayout>
      </PageLayout>
    );
  }

  // Show area selector on calendar screen when there are experiences
  const alwaysShowAreaSelector = safeParse(restaurant?.data?.bookingWidgetSettings)?.widgetSettings
    ?.settingsDetails?.alwaysShowAreaSelector;

  const getWidgetContent = () => {
    if (isUsingExperiences && alwaysShowAreaSelector) {
      return (
        <WidgetLayout>
          <Spacer direction='vertical' gap='m' style={{ width: '100%' }}>
            {!isEmpty(message) && (
              <Typography centered>
                <div dangerouslySetInnerHTML={{ __html: message }} />
              </Typography>
            )}
            <Divider color={theme.colors.dividerColor} />
            <SelectADate />
            <Spacer direction='vertical' gap={32}>
              <Box style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
                <Box
                  style={{
                    // ...getFixedGridColumns(showAreas ? 2 : 1, 12),
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'space-around',
                    // maxWidth: '480px',
                    width: '100%',
                  }}
                >
                  <Box>
                    <Guests />
                  </Box>

                  {/* note: show areas if there are multiple different areas to choose from */}
                  {showAreas && (
                    <Box style={{ display: 'flex', justifyContent: 'center' }}>
                      <Areas />
                    </Box>
                  )}
                </Box>
              </Box>

              <Divider color={theme.colors.dividerColor} />
              {isUsingExperiences &&
                (experiences?.pending ? (
                  <Skeleton height={240} variant='rect' style={{ borderRadius: '6px' }} />
                ) : (
                  <SelectExperience />
                ))}

              {showAvailableTimes && <NextAvailableTimes />}

              <CustomBookingFields
                fields={firstPageCustomFields}
                values={booking.customBookingFieldValues}
                errors={customFieldErrors}
                onChange={updateCustomBookingField}
              />

              <Divider color={theme.colors.dividerColor} />

              <Box
                ref={nextButtonRef}
                style={{
                  width: '100%',
                  minWidth: '144px',
                  marginLeft: 'auto',
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  onClick={submit}
                  size='large'
                  style={{ minWidth: '144px', marginLeft: 'auto' }}
                  disabled={nextButtonDisabled}
                  color='primary'
                  fullWidth={isMobile}
                  loading={isSubmitting}
                >
                  Next
                </Button>
              </Box>
            </Spacer>
          </Spacer>
        </WidgetLayout>
      );
    }

    return (
      <WidgetLayout>
        <Spacer direction='vertical' gap='m' style={{ width: '100%' }}>
          {!isEmpty(message) && (
            <Typography centered>
              <div dangerouslySetInnerHTML={{ __html: message }} />
            </Typography>
          )}

          {(!isEmpty(message) || isUsingExperiences) && (
            <Divider color={theme.colors.dividerColor} />
          )}

          {isUsingExperiences && (
            <>
              <Box
                style={{
                  padding: '0 12px',
                  ...(isSpecificEventDate && {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }),
                }}
              >
                <Guests />

                {isSpecificEventDate && (
                  <Box>{formatDateWithYear(eventDates?.[0], 'Do of MMMM')}</Box>
                )}
              </Box>
              <Divider color={theme.colors.dividerColor} />
              <SelectADate />
            </>
          )}

          {!isUsingExperiences && (
            <>
              <SelectADate />
              {isMobile ? (
                <Spacer direction='vertical' gap='m'>
                  <Guests />

                  <Divider color={theme.colors.dividerColor} />

                  <Box style={{ ...getFixedGridColumns(showAreas ? 1 : 0, 12) }}>
                    {/* note: show areas if there are multiple different areas to choose from */}
                    {showAreas && (
                      <Box style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Areas />
                      </Box>
                    )}
                  </Box>
                </Spacer>
              ) : (
                <Box style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
                  <Box
                    style={{
                      // ...getFixedGridColumns(showAreas ? 2 : 1, 12),
                      display: 'flex',
                      gap: '12px',
                      justifyContent: 'space-around',
                      // maxWidth: '480px',
                      width: '100%',
                    }}
                  >
                    <Box>
                      <Guests />
                    </Box>

                    {/* note: show areas if there are multiple different areas to choose from */}
                    {showAreas && (
                      <Box style={{ display: 'flex', justifyContent: 'center' }}>
                        <Areas />
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              <Divider color={theme.colors.dividerColor} />
            </>
          )}

          <Spacer direction='vertical' gap={32}>
            {isUsingExperiences &&
              (experiences?.pending ? (
                <Skeleton height={240} variant='rect' style={{ borderRadius: '6px' }} />
              ) : (
                <SelectExperience />
              ))}

            {showAvailableTimes && <NextAvailableTimes />}

            <CustomBookingFields
              fields={firstPageCustomFields}
              values={booking.customBookingFieldValues}
              errors={customFieldErrors}
              onChange={updateCustomBookingField}
            />

            <Divider color={theme.colors.dividerColor} />

            <Box
              ref={nextButtonRef}
              style={{
                width: '100%',
                minWidth: '144px',
                marginLeft: 'auto',
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                onClick={submit}
                size='large'
                style={{ minWidth: '144px', marginLeft: 'auto' }}
                disabled={nextButtonDisabled}
                color='primary'
                fullWidth={isMobile}
                loading={isSubmitting}
              >
                Next
              </Button>
            </Box>
          </Spacer>
        </Spacer>
      </WidgetLayout>
    );
  };

  return (
    <PageLayout>
      {(booking.editBooking || booking.editWaitlist) && (
        <WidgetLayout size='small'>
          <Spacer
            direction={isMobile ? 'vertical' : 'horizontal'}
            style={{ alignItems: isMobile ? 'flex-start' : 'center' }}
            className={classes.editBooking}
          >
            <Typography variant='small' style={{ fontWeight: theme.fontWeights.bold }}>
              Edit {obeeBooking.data.status === STATUS_TYPES.WAITLIST ? 'waitlist' : 'booking'}
            </Typography>

            <Spacer centered>
              <IconDate fill={restaurant.data.iconBgColour} />
              <Typography variant='small'>
                {formatDateToMoment(obeeBooking.data.date).format('ddd Do of MMM')}
              </Typography>
            </Spacer>
            <Spacer centered>
              <IconGuests fill={restaurant.data.iconBgColour} />
              <Typography variant='small'>{`${obeeBooking.data.size} ${
                obeeBooking.data.size > 1 ? 'guests' : 'guest'
              }`}</Typography>
            </Spacer>
            <Spacer centered>
              <IconTime fill={restaurant.data.iconBgColour} />
              <Typography variant='small'>{`${
                obeeBooking.data.status === STATUS_TYPES.WAITLIST
                  ? 'Requested time'
                  : restaurant?.data?.arrivalTimeLabel
              } ${minutesToTimeString(obeeBooking.data.time)}`}</Typography>
            </Spacer>
          </Spacer>
        </WidgetLayout>
      )}

      {getWidgetContent()}

      {(obeeMultiVenueAvailabilitySuggestions.pending || showMultiVenue) && (
        <Spacer gap='l' style={{ color: restaurant.data.bodyTextColour, margin: '12px' }}>
          {obeeMultiVenueAvailabilitySuggestions.pending && (
            <CircularProgress color='inherit' size={18} />
          )}

          <Typography variant='h4'>
            {obeeMultiVenueAvailabilitySuggestions.pending ? 'Fetching' : 'Discover'} availability
            at our other venues
          </Typography>

          {obeeMultiVenueAvailabilitySuggestions.success && (
            <Button
              variant='outlined'
              style={{
                color: restaurant.data.bodyButtonTextColour,
                borderColor: restaurant.data.bodyButtonBorderColour,
              }}
              onClick={() => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
            >
              Explore
            </Button>
          )}
        </Spacer>
      )}

      {showMultiVenue && (
        <Box ref={ref}>
          {multiVenueRestIds.map((multiVenueRestId) => (
            <WidgetLayout key={multiVenueRestId}>
              <Spacer direction='vertical' gap='l' style={{ width: '100%' }}>
                <MultiVenueNextAvailableTimes restId={multiVenueRestId} />
              </Spacer>
            </WidgetLayout>
          ))}
        </Box>
      )}
    </PageLayout>
  );
};

Availability.propTypes = {
  booking: bookingPropTypes.isRequired,
  obeeMultiVenueAvailabilitySuggestions: obeeMultiVenueAvailabilitySuggestionsPropTypes.isRequired,
  obeeBooking: obeeBookingPropTypes.isRequired,
  restaurant: restaurantPropTypes.isRequired,
  restaurants: restaurantsPropTypes.isRequired,
  restaurantsByIds: PropTypes.func.isRequired,
  experiences: experiencesPropTypes.isRequired,
  obeeAvailabilitySuggestions: obeeAvailabilitySuggestionsPropTypes.isRequired,
  updateBookingStep: PropTypes.func.isRequired,
  updateBooking: PropTypes.func.isRequired,
  holdBooking: PropTypes.func.isRequired,
  freeBooking: PropTypes.func.isRequired,
  holdExperiences: PropTypes.func.isRequired,
  holdAccommodation: PropTypes.func.isRequired,
  addonGroups: addonGroupsPropTypes.isRequired,
  fetchAddons: PropTypes.func.isRequired,
  event: eventPropTypes.isRequired,
  isEventMode: PropTypes.bool.isRequired,
  fetchBookingTerms: PropTypes.func.isRequired,
  bookingTerms: bookingTermsPropTypes.isRequired,
};

const mapStateToProps = (state) => ({
  booking: state.booking,
  obeeMultiVenueAvailabilitySuggestions: state.obeeMultiVenueAvailabilitySuggestions,
  obeeBooking: state.obeeBooking,
  restaurant: state.restaurant,
  restaurants: state.restaurants,
  experiences: state.experiences,
  obeeAvailabilitySuggestions: state.obeeAvailabilitySuggestions,
  addonGroups: state.addonGroups,
  event: state.event,
  bookingTerms: state.bookingTerms,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      restaurantsByIds: restaurantsByIdsAction,
      updateBookingStep: updateBookingStepAction,
      updateBooking: updateBookingAction,
      holdBooking: holdBookingAction,
      freeBooking: freeBookingAction,
      holdExperiences: holdExperiencesAction,
      holdAccommodation: holdAccommodationAction,
      fetchAddons: fetchAddonsAction,
      fetchBookingTerms: fetchBookingTermsAction,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(Availability);
